import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useAsync, useSession, useToast } from '../../lib/store';
import type { TaskBoard, TasksResponse } from '../../lib/types';
import { Bar, Button, Panel } from '../ui';
import { formatNumber } from '../../lib/format';

/**
 * Daily and weekly tasks — the predictable earning floor.
 *
 * Each finished task pays on its own, so a half-finished board is still worth
 * claiming. That is the whole point: the audit found a player who did not
 * finish everything earned nothing at all, and "I can never afford anything"
 * is the feeling this panel exists to remove.
 *
 * Progress is computed server-side from rows the game already writes, so
 * nothing here can be nudged by the client — this component only renders and
 * asks to be paid.
 */
export function TasksPanel() {
  const { push } = useToast();
  const { refresh } = useSession();
  const state = useAsync<TasksResponse>(() => api.get('/economy/tasks'));
  const [busy, setBusy] = useState<string | null>(null);

  if (!state.data) return null;

  async function claim(period: 'daily' | 'weekly') {
    setBusy(period);
    try {
      const result = await api.post<{ coins: number }>('/economy/tasks/claim', { period });
      push('success', `+${formatNumber(result.coins)} coins`, '◆');
      state.reload();
      void refresh();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not claim.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="stack">
      <Board board={state.data.daily} busy={busy === 'daily'} onClaim={() => void claim('daily')} />
      <Board board={state.data.weekly} busy={busy === 'weekly'} onClaim={() => void claim('weekly')} />
    </div>
  );
}

function Board({
  board,
  busy,
  onClaim,
}: {
  board: TaskBoard;
  busy: boolean;
  onClaim: () => void;
}) {
  const done = board.tasks.filter((task) => task.complete).length;

  return (
    // Collapsible, and folded by default once the board is finished with.
    // Seven daily tasks plus four weeklies is most of a screen on a phone, and
    // a player who has already done them is scrolling past a wall of ticks to
    // reach whatever they actually came for. The hint stays visible folded, so
    // "3 / 7" and any unclaimed coins are readable without expanding.
    <Panel
      collapsibleKey={`tasks.${board.period}`}
      title={board.period === 'daily' ? '◆ Daily tasks' : '◆ Weekly tasks'}
      hint={
        <span className="panel__hint">
          {board.claimable > 0 && (
            <strong style={{ color: 'var(--gold-dark)' }}>
              +{formatNumber(board.claimable)} to claim ·{' '}
            </strong>
          )}
          {done} / {board.tasks.length}
        </span>
      }
    >
      <div className="stack-sm">
        {board.tasks.map((task) => (
          <div key={task.key} className="task-row">
            <span className={`task-row__tick ${task.complete ? 'task-row__tick--done' : ''}`}>
              {task.complete ? '✓' : ''}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="row row--between">
                <strong className="tiny">{task.name}</strong>
                <span className="tiny muted">
                  {task.claimed ? 'claimed' : `+${formatNumber(task.coins)}`}
                </span>
              </div>
              <Bar ratio={task.goal > 0 ? task.progress / task.goal : 0} variant="exp" />
              <div className="row row--between tiny muted">
                <span>{task.detail}</span>
                <span className="mono">
                  {task.progress} / {task.goal}
                </span>
              </div>
            </div>
          </div>
        ))}

        {/* The completion bonus is a pull, not a gate — every task above pays
            whether or not this is reached. */}
        <div className="row row--between tiny">
          <span className={board.allComplete ? '' : 'muted'}>
            All {board.tasks.length} complete: <strong>+{formatNumber(board.completionBonus)}</strong>
          </span>
        </div>

        <Button
          variant="gold"
          block
          loading={busy}
          disabled={board.claimable <= 0}
          onClick={onClaim}
        >
          {board.claimable > 0
            ? `Claim ${formatNumber(board.claimable)} coins`
            : done === 0
              ? 'Nothing finished yet'
              : 'Nothing left to claim'}
        </Button>
      </div>
    </Panel>
  );
}
